import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useLocation } from 'react-router-dom';
import _isEmpty from 'lodash/isEmpty';
import _isFunction from 'lodash/isFunction';
import Isvg from 'react-inlinesvg';

import { PFE_ICONS } from 'constants/icons';
import { classNameWithModifiers as withMods } from 'helpers/class-name';
import { makePath, addQueryParamsToPath } from 'helpers/link-generation';
import { Dropdown } from 'components/Dropdown';

import { makeShouldRender, NAV_CONFIG } from './config';
import NavLink from './NavLink';
import NavDropdownToggle from './NavDropdownToggle';
import NavDropdownMenu from './NavDropdownMenu';

import './Sidenav.scss';
import './nav.scss';

const Sidenav = ({
  activeVenueId = 0,
  activeClassId,
  hideNavVisibility,
  isAuthenticated = false,
  isCollapsed = false,
  locale,
  mobileNavOpen = false,
  toggleDesktopNavExpandCollapse,
  userInfo,
}) => {
  const container = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  const [shouldRenderResults, setShouldRenderResults] = useState({});
  const location = useLocation();
  const { pathname: activePath, search: activeSearchParams } = location;
  const userId = userInfo?.userId;

  const navConfig = useMemo(() => {
    const navPathObject = { venueId: activeVenueId, classId: activeClassId, locale, userId };
    const decorateConfigItem = config => {
      const { path = '', testPath, children = [], searchParams } = config;
      const basePath = _isFunction(path) ? path(navPathObject) : path;
      const resolvedPath = searchParams ? addQueryParamsToPath(basePath, activeSearchParams, searchParams) : basePath;
      const updatedChildren = children.map(decorateConfigItem);
      const isActive = testPath ? testPath(activePath) : activePath.startsWith(resolvedPath);
      const hasActiveChild = updatedChildren.some(cfg => cfg.isActive);

      return {
        ...config,
        resolvedPath,
        children: updatedChildren,
        hasActiveChild,
        isActive: hasActiveChild || isActive,
      };
    };

    return NAV_CONFIG.map(decorateConfigItem);
  }, [activeVenueId, activeClassId, locale, userId, activePath, activeSearchParams]);

  const handleMouseEnter = () => {
    if (isCollapsed) {
      setIsHovered(true);
    }
  };

  const handleMouseLeave = () => {
    if (isCollapsed) {
      setIsHovered(false);
    }
  };

  const shouldRender = makeShouldRender(userInfo, activeVenueId, locale);

  useEffect(() => {
    const { current: containerElement } = container;

    if (containerElement) {
      containerElement.addEventListener('mouseenter', handleMouseEnter);
      containerElement.addEventListener('mouseleave', handleMouseLeave);
    }

    const fetchShouldRenderResults = async () => {
      const results = {};
      await Promise.all(
        navConfig.map(async config => {
          results[config.message.id] = await shouldRender(config);
        })
      );
      setShouldRenderResults(results);
    };

    fetchShouldRenderResults();

    return () => {
      if (containerElement) {
        containerElement.removeEventListener('mouseenter', handleMouseEnter);
        containerElement.removeEventListener('mouseleave', handleMouseLeave);
      }
    };
  }, [activeVenueId, isCollapsed, navConfig, userInfo]);

  const renderNavItem = config => {
    const { children, message, hasActiveChild, isActive } = config;
    const renderAsCollapsed = isCollapsed && !isHovered;

    if (!shouldRenderResults[message.id]) return null;

    if (_isEmpty(children)) {
      return (
        <NavLink
          key={message.id}
          className="nav__link"
          handleClick={hideNavVisibility}
          showText={!renderAsCollapsed}
          config={config}
        />
      );
    }

    const isOpen = !isCollapsed && (isActive || hasActiveChild);

    return (
      <Dropdown
        key={message.id}
        className="nav__dropdown"
        Toggle={NavDropdownToggle}
        Content={NavDropdownMenu}
        attachment="left"
        closeOnClick={false}
        noStyle
        isOpen={isOpen}
        isCollapsed={renderAsCollapsed}
        hideMobileNavOpen={hideNavVisibility}
        usePortal={false}
        config={config}
        shouldRender={shouldRender}
      />
    );
  };

  const renderAsCollapsed = isCollapsed && !isHovered;
  const classes = withMods('sidenav', {
    'is-collapsed': renderAsCollapsed,
    'is-open': mobileNavOpen,
  });
  const logoClasses = withMods('nav__logo', { 'is-collapsed': renderAsCollapsed });

  if (!isAuthenticated) return null;

  return (
    <nav className={classes} id="sidenav">
      <div ref={container} className="nav__container">
        <div className="nav__logo-container">
          <Link className={logoClasses} to={makePath.home(activeVenueId)}>
            {renderAsCollapsed ? <Isvg src={PFE_ICONS.logo} /> : <Isvg src={PFE_ICONS.logotype} />}
          </Link>
        </div>
        <nav className="nav">{navConfig.map(config => renderNavItem(config))}</nav>
      </div>
      <div className="sidenav__resize-handle" onClick={toggleDesktopNavExpandCollapse} role="presentation" />
    </nav>
  );
};

Sidenav.propTypes = {
  activeVenueId: PropTypes.number,
  activeClassId: PropTypes.number,
  hideNavVisibility: PropTypes.func,
  isAuthenticated: PropTypes.bool,
  isCollapsed: PropTypes.bool,
  locale: PropTypes.string,
  mobileNavOpen: PropTypes.bool,
  toggleDesktopNavExpandCollapse: PropTypes.func,
  userInfo: PropTypes.shape({
    userId: PropTypes.number,
  }),
};

export default Sidenav;
